/* @refresh reset */
import { createContext, useContext, useReducer, useCallback, useEffect, useMemo, useRef } from 'react'
import {
  FlowContextType,
  FlowState,
  FlowConfig,
  FlowAction,
  FlowData,
  ValidationResult,
  FlowCompleteOptions
} from './types'
import { debug } from '../../utils/debug'
import { scrollToTopAfterRender } from '../../utils/scrollHelpers'

// Create a context with the base FlowData type
const FlowContext = createContext<FlowContextType | undefined>(undefined)

const createInitialState = <T extends FlowData>(config: FlowConfig<T>): FlowState<T> => {
  const initialStepIndex = config.initialStep ?? 0

  // Mark all steps before initialStep as completed and visited
  const initialCompletedSteps: string[] = []
  const initialVisitedSteps: string[] = []

  if (initialStepIndex > 0) {
    for (let i = 0; i < initialStepIndex; i++) {
      const stepId = config.steps[i]?.id
      if (stepId) {
        initialCompletedSteps.push(stepId)
        initialVisitedSteps.push(stepId)
      }
    }
  }

  return {
    currentStep: initialStepIndex,
    completedSteps: initialCompletedSteps,
    visitedSteps: initialVisitedSteps,
    skippedSteps: [],
    data: (config.initialData || {}) as T,
    errors: {},
    warnings: {},
    isValid: true,
    isLoading: false,
    loadingMessage: undefined
  }
}

// Generic flow reducer with improved type safety
function flowReducer<T extends FlowData>(state: FlowState<T>, action: FlowAction<T>): FlowState<T> {
  switch (action.type) {
    case 'NEXT_STEP': {
      const nextStep = state.currentStep + 1
      // Note: We'll handle the step bounds check in the action creator
      return {
        ...state,
        currentStep: nextStep
      }
    }

    case 'PREVIOUS_STEP': {
      const prevStep = state.currentStep - 1
      if (prevStep >= 0) {
        return {
          ...state,
          currentStep: prevStep
        }
      }
      return state
    }

    case 'GO_TO_STEP': {
      const targetStep = Math.max(0, Math.min(action.payload, state.completedSteps.length))
      return {
        ...state,
        currentStep: targetStep
      }
    }

    case 'UPDATE_DATA': {
      const newData = {
        ...state.data,
        [action.payload.key]: action.payload.value
      }
      return {
        ...state,
        data: newData
      }
    }

    case 'UPDATE_DATA_BATCH': {
      return { ...state, data: { ...state.data, ...action.payload } }
    }

    case 'SET_ERRORS': {
      const newErrors = {
        ...state.errors,
        [action.payload.stepId]: action.payload.errors
      }
      return {
        ...state,
        errors: newErrors,
        isValid: Object.values(newErrors).every((errors) => errors.length === 0)
      }
    }

    case 'SET_WARNINGS': {
      return {
        ...state,
        warnings: {
          ...state.warnings,
          [action.payload.stepId]: action.payload.warnings
        }
      }
    }

    case 'COMPLETE_STEP': {
      return {
        ...state,
        completedSteps: [...new Set([...state.completedSteps, action.payload])]
      }
    }

    case 'VISIT_STEP': {
      return {
        ...state,
        visitedSteps: [...new Set([...state.visitedSteps, action.payload])]
      }
    }

    case 'SKIP_STEP': {
      return {
        ...state,
        skippedSteps: [...new Set([...state.skippedSteps, action.payload])]
      }
    }

    case 'VALIDATE_STEP': {
      const { stepId, result } = action.payload
      const newErrors = {
        ...state.errors,
        [stepId]: result.errors
      }
      const newWarnings = {
        ...state.warnings,
        [stepId]: result.warnings || []
      }
      return {
        ...state,
        errors: newErrors,
        warnings: newWarnings,
        isValid: Object.values(newErrors).every((errors) => errors.length === 0)
      }
    }

    case 'RESET_FLOW': {
      return state
    }

    case 'SET_LOADING': {
      return {
        ...state,
        isLoading: action.payload.isLoading,
        loadingMessage: action.payload.message
      }
    }

    default:
      return state
  }
}

interface FlowProviderProps<T extends FlowData> {
  children: React.ReactNode
  config: FlowConfig<T>
  onComplete?: (data: T, options?: FlowCompleteOptions) => void
  onSaveDraft?: (
    data: T,
    stepId: string,
    helpers: {
      updateData: <K extends keyof T>(key: K, value: T[K]) => void
      setLoading: (isLoading: boolean, message?: string) => void
    },
    options?: { silent?: boolean }
  ) => void | Promise<void>
  onDeleteDraft?: () => void
  onStepChange?: (stepIndex: number, stepId: string) => void
  onValidationChange?: (isValid: boolean, errors: Record<string, string[]>) => void
}

export function FlowProvider<T extends FlowData>({
  children,
  config,
  onComplete,
  onSaveDraft,
  onDeleteDraft,
  onStepChange,
  onValidationChange
}: FlowProviderProps<T>) {
  const [state, dispatch] = useReducer(flowReducer<T>, createInitialState(config))

  // Call onStepChange on initial render only
  // Using useLayoutEffect to ensure it runs before children render
  useEffect(() => {
    const initialStepId = config.steps[state.currentStep]?.id
    onStepChange?.(state.currentStep, initialStepId || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Intentionally empty - only run on mount

  // Call onValidationChange when validation state changes
  // Use a ref to track previous values to avoid unnecessary calls
  const prevValidationRef = useRef<{ isValid: boolean; errors: Record<string, string[]> }>({
    isValid: state.isValid,
    errors: state.errors
  })
  const prevErrorsRef = useRef(state.errors)

  // Helper to shallow compare errors objects (moved outside to avoid dependency issues)
  const areErrorsEqual = (prev: Record<string, string[]>, current: Record<string, string[]>): boolean => {
    const prevKeys = Object.keys(prev)
    const currentKeys = Object.keys(current)

    if (prevKeys.length !== currentKeys.length) {
      return false
    }

    for (const key of prevKeys) {
      const prevErrors = prev[key]
      const currentErrors = current[key]

      if (!currentErrors || prevErrors.length !== currentErrors.length) {
        return false
      }

      // Compare arrays shallowly
      for (let i = 0; i < prevErrors.length; i++) {
        if (prevErrors[i] !== currentErrors[i]) {
          return false
        }
      }
    }

    return true
  }

  useEffect(() => {
    const prev = prevValidationRef.current
    const isValidChanged = prev.isValid !== state.isValid
    const errorsChanged = !areErrorsEqual(prevErrorsRef.current, state.errors)

    // Only call if validation state actually changed
    if (isValidChanged || errorsChanged) {
      prevValidationRef.current = { isValid: state.isValid, errors: state.errors }
      prevErrorsRef.current = state.errors
      onValidationChange?.(state.isValid, state.errors)
    }
  }, [state.isValid, state.errors, onValidationChange])

  // Centralized validation function
  const validateStep = useCallback(
    (stepId: string, data: T): ValidationResult => {
      const step = config.steps.find((s) => s.id === stepId)
      if (!step) {
        return { isValid: true, errors: [], warnings: [] }
      }

      // Use step-specific validation if available
      if (step.validation) {
        return step.validation(data, stepId)
      }

      // Use global validation if available
      if (config.onValidate) {
        return config.onValidate(data, stepId)
      }

      return { isValid: true, errors: [], warnings: [] }
    },
    [config]
  )

  // Validate current step
  // Note: config.steps is already covered by validateStep dependency on config
  const validateCurrentStep = useCallback((): ValidationResult => {
    const currentStep = config.steps[state.currentStep]
    if (!currentStep) {
      return { isValid: true, errors: [], warnings: [] }
    }

    // Use onProceedValidation if available, otherwise fall back to regular validation
    if (currentStep.onProceedValidation) {
      return currentStep.onProceedValidation(state.data)
    }

    return validateStep(currentStep.id, state.data)
  }, [state.currentStep, state.data, validateStep])

  const currentStepId = config.steps[state.currentStep]?.id
  const isCurrentStepShowingErrors = Boolean(currentStepId && state.errors[currentStepId]?.length)

  // Errors shown after a Next press follow every edit, so they go away as soon as the step is fixed.
  useEffect(() => {
    if (!currentStepId || !isCurrentStepShowingErrors) return

    dispatch({ type: 'VALIDATE_STEP', payload: { stepId: currentStepId, result: validateCurrentStep() } })
  }, [currentStepId, isCurrentStepShowingErrors, validateCurrentStep])

  // Validate all steps
  const validateAllSteps = useCallback((): ValidationResult => {
    const allErrors: string[] = []
    const allWarnings: string[] = []

    config.steps.forEach((step) => {
      const result = validateStep(step.id, state.data)
      allErrors.push(...result.errors)
      if (result.warnings) {
        allWarnings.push(...result.warnings)
      }
    })

    return {
      isValid: allErrors.length === 0,
      errors: allErrors,
      warnings: allWarnings
    }
  }, [config.steps, state.data, validateStep])

  // Define updateData BEFORE goToNext since goToNext uses it
  const updateData = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    dispatch({ type: 'UPDATE_DATA', payload: { key, value } })
  }, [])

  const batchUpdateData = useCallback((partial: Partial<T>) => {
    dispatch({ type: 'UPDATE_DATA_BATCH', payload: partial as Partial<FlowData> })
  }, [])

  // Extract setLoadingFn to stable callback to avoid inline function creation
  const setLoadingFn = useCallback((isLoading: boolean, message?: string) => {
    dispatch({ type: 'SET_LOADING', payload: { isLoading, message } })
  }, [])

  const finishFlow = useCallback(
    (options?: FlowCompleteOptions) => {
      dispatch({ type: 'SET_LOADING', payload: { isLoading: true, message: 'Launching sprint...' } })
      onComplete?.(state.data, options)
    },
    [state.data, onComplete]
  )

  const completeFlow = useCallback(
    (options?: FlowCompleteOptions) => {
      const currentStepId = config.steps[state.currentStep]?.id
      if (!currentStepId) return

      if (state.currentStep !== config.steps.length - 1) {
        return
      }

      dispatch({ type: 'VISIT_STEP', payload: currentStepId })

      const validationResult = validateCurrentStep()
      dispatch({
        type: 'VALIDATE_STEP',
        payload: { stepId: currentStepId, result: validationResult }
      })

      if (!validationResult.isValid) {
        return
      }

      const shouldSkipSave = currentStepId === 'define-purpose'
      if (onSaveDraft && !shouldSkipSave) {
        try {
          onSaveDraft(state.data, currentStepId, { updateData, setLoading: setLoadingFn }, { silent: true })
        } catch (error) {
          debug.error('Error calling saveDraft:', error)
        }
      }

      dispatch({ type: 'COMPLETE_STEP', payload: currentStepId })
      finishFlow(options)
    },
    [
      state.currentStep,
      state.data,
      config.steps,
      validateCurrentStep,
      onSaveDraft,
      updateData,
      setLoadingFn,
      finishFlow
    ]
  )

  const goToNext = useCallback(() => {
    const currentStepId = config.steps[state.currentStep]?.id
    if (!currentStepId) return

    // Mark step as visited
    dispatch({ type: 'VISIT_STEP', payload: currentStepId })

    // Validate current step
    const validationResult = validateCurrentStep()

    dispatch({
      type: 'VALIDATE_STEP',
      payload: { stepId: currentStepId, result: validationResult }
    })

    // Don't proceed if there are validation errors
    if (!validationResult.isValid) {
      return
    }

    // Save draft before proceeding to next step
    // This ensures customizations and other changes are persisted
    // Note: We call saveDraft but don't await - it's fire-and-forget to avoid blocking navigation
    // The save will happen in the background and any errors will be handled by the save handler
    // Skip save when going from step 1 (define-purpose) to step 2 (content-step) to avoid flash
    const shouldSkipSave = currentStepId === 'define-purpose'
    if (onSaveDraft && !shouldSkipSave) {
      try {
        // Fire and forget - don't block navigation
        // Pass silent: true to indicate this is an auto-save from Next button
        // For step 3, always use silent: true to prevent loading indication
        // Note: setLoading is available in the context, but auto-save doesn't need to manage loading state
        onSaveDraft(state.data, currentStepId, { updateData, setLoading: setLoadingFn }, { silent: true })
      } catch (error) {
        // Handle sync errors
        debug.error('Error calling saveDraft:', error)
      }
    }

    // Mark step as completed
    dispatch({ type: 'COMPLETE_STEP', payload: currentStepId })

    // Move to next step or complete
    if (state.currentStep < config.steps.length - 1) {
      const nextStepIndex = state.currentStep + 1
      dispatch({ type: 'NEXT_STEP' })
      const nextStepId = config.steps[nextStepIndex]?.id
      onStepChange?.(nextStepIndex, nextStepId || '')
      // Scroll to top after React updates DOM
      scrollToTopAfterRender()
    } else {
      finishFlow()
    }
  }, [
    state.currentStep,
    state.data,
    config.steps,
    validateCurrentStep,
    finishFlow,
    onStepChange,
    onSaveDraft,
    updateData,
    setLoadingFn
  ])

  /**
   * Reflection facilitate flow: after async scope lock + generateReflectionData, plain
   * {@link goToNext} can fail validation if `validateCurrentStep` sees a stale snapshot of
   * `state.data`. Scope is already persisted; advance without re-validating select-scope.
   */
  const goToNextAfterScopeLock = useCallback(() => {
    const currentStepId = config.steps[state.currentStep]?.id
    if (!currentStepId) return

    if (currentStepId !== 'select-scope') {
      goToNext()
      return
    }

    dispatch({ type: 'VISIT_STEP', payload: currentStepId })

    dispatch({
      type: 'VALIDATE_STEP',
      payload: { stepId: currentStepId, result: { isValid: true, errors: [] } }
    })

    if (onSaveDraft) {
      try {
        onSaveDraft(state.data, currentStepId, { updateData, setLoading: setLoadingFn }, { silent: true })
      } catch (error) {
        debug.error('Error calling saveDraft:', error)
      }
    }

    dispatch({ type: 'COMPLETE_STEP', payload: currentStepId })

    if (state.currentStep < config.steps.length - 1) {
      const nextStepIndex = state.currentStep + 1
      dispatch({ type: 'NEXT_STEP' })
      const nextStepId = config.steps[nextStepIndex]?.id
      onStepChange?.(nextStepIndex, nextStepId || '')
      scrollToTopAfterRender()
    } else {
      finishFlow()
    }
  }, [
    state.currentStep,
    state.data,
    config.steps,
    goToNext,
    finishFlow,
    onStepChange,
    onSaveDraft,
    updateData,
    setLoadingFn
  ])

  const goToPrevious = useCallback(() => {
    if (state.currentStep > 0) {
      dispatch({ type: 'PREVIOUS_STEP' })
      const prevStepId = config.steps[state.currentStep - 1]?.id
      onStepChange?.(state.currentStep - 1, prevStepId || '')
      // Note: Scroll to top removed for Previous button - user requested
    }
  }, [state.currentStep, config.steps, onStepChange])

  const goToStep = useCallback(
    (stepIndex: number) => {
      dispatch({ type: 'GO_TO_STEP', payload: stepIndex })
      const stepId = config.steps[stepIndex]?.id
      onStepChange?.(stepIndex, stepId || '')
      // Scroll to top after React updates DOM
      scrollToTopAfterRender()
    },
    [config.steps, onStepChange]
  )

  const setErrors = useCallback((stepId: string, errors: string[]) => {
    dispatch({ type: 'SET_ERRORS', payload: { stepId, errors } })
  }, [])

  const setWarnings = useCallback((stepId: string, warnings: string[]) => {
    dispatch({ type: 'SET_WARNINGS', payload: { stepId, warnings } })
  }, [])

  const isStepValid = useCallback(
    (stepId: string) => {
      const stepErrors = state.errors[stepId]
      return !stepErrors || stepErrors.length === 0
    },
    [state.errors]
  )

  const isStepCompleted = useCallback(
    (stepId: string) => {
      return state.completedSteps.includes(stepId)
    },
    [state.completedSteps]
  )

  const isStepVisited = useCallback(
    (stepId: string) => {
      return state.visitedSteps.includes(stepId)
    },
    [state.visitedSteps]
  )

  const isStepSkipped = useCallback(
    (stepId: string) => {
      return state.skippedSteps.includes(stepId)
    },
    [state.skippedSteps]
  )

  const canProceed = useCallback(() => {
    const currentStepConfig = config.steps[state.currentStep]
    if (!currentStepConfig) return false

    // Check if step depends on another step
    if (currentStepConfig.rules?.dependsOn) {
      const dependsOnStepId = currentStepConfig.rules.dependsOn
      if (!state.completedSteps.includes(dependsOnStepId)) {
        return false
      }
    }

    // Always validate the current step to determine if we can proceed
    const validationResult = validateStep(currentStepConfig.id, state.data)

    if (!validationResult.isValid) {
      return false
    }

    return true
  }, [state.currentStep, state.completedSteps, state.data, config.steps, validateStep])

  const canGoBack = useCallback(() => {
    return state.currentStep > 0
  }, [state.currentStep])

  const setLoading = useCallback((isLoading: boolean, message?: string) => {
    dispatch({ type: 'SET_LOADING', payload: { isLoading, message } })
  }, [])

  const saveDraft = useCallback(() => {
    const currentStepConfig = config.steps[state.currentStep]
    const stepId = currentStepConfig?.id ?? ''
    const dataRecord = state.data as Record<string, unknown>
    const isArchivedReflection = dataRecord.reflection_type !== undefined && dataRecord.status === 'archived'
    const loadingMessage = isArchivedReflection ? 'Saving...' : 'Saving draft...'

    // Manual save button should ALWAYS show loading indication
    // Only auto-saves (from goToNext) should be silent on step 3
    // Set loading state
    dispatch({ type: 'SET_LOADING', payload: { isLoading: true, message: loadingMessage } })

    // Call the save draft handler with current data, stepId and helpers (updateData and setLoading)
    // Manual saves should never be silent - user expects feedback
    onSaveDraft?.(state.data, stepId, { updateData, setLoading }, { silent: false })
  }, [state.data, state.currentStep, config.steps, onSaveDraft, updateData, setLoading])

  const deleteDraft = useCallback(() => {
    // Set loading state
    dispatch({ type: 'SET_LOADING', payload: { isLoading: true, message: 'Deleting draft...' } })

    // Call the delete draft handler
    onDeleteDraft?.()
  }, [onDeleteDraft])

  const resetFlow = useCallback(() => {
    // Reset to initial state
    dispatch({ type: 'RESET_FLOW' })
  }, [])

  const value = useMemo(
    () => ({
      state,
      config,
      dispatch,
      goToNext,
      completeFlow,
      goToNextAfterScopeLock,
      goToPrevious,
      goToStep,
      updateData,
      batchUpdateData,
      setErrors,
      setWarnings,
      isStepValid,
      isStepCompleted,
      isStepVisited,
      isStepSkipped,
      canProceed,
      canGoBack,
      saveDraft,
      deleteDraft,
      resetFlow,
      setLoading,
      validateCurrentStep,
      validateAllSteps
    }),
    [
      state,
      config,
      dispatch,
      goToNext,
      completeFlow,
      goToNextAfterScopeLock,
      goToPrevious,
      goToStep,
      updateData,
      batchUpdateData,
      setErrors,
      setWarnings,
      isStepValid,
      isStepCompleted,
      isStepVisited,
      isStepSkipped,
      canProceed,
      canGoBack,
      saveDraft,
      deleteDraft,
      resetFlow,
      setLoading,
      validateCurrentStep,
      validateAllSteps
    ]
  )

  // FlowContext uses a base type; consumers cast to their specific T via useFlow<T>()
  return <FlowContext.Provider value={value as unknown as FlowContextType}>{children}</FlowContext.Provider>
}

export function useFlow<T extends FlowData = FlowData>(): FlowContextType<T> {
  const context = useContext(FlowContext)
  if (context === undefined) {
    throw new Error('useFlow must be used within a FlowProvider')
  }
  return context as unknown as FlowContextType<T>
}
